import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Sword, ShieldPlus, Wallet, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * NextBestAction — deterministic action cards on the dashboard.
 *
 * Calls GET /api/coach/next-actions, renders 1–3 horizontal swipeable cards.
 * Different kinds tie to different in-place actions: "defeat" defeats a sub
 * directly, "vault"/"upgrade" route, "coach" opens Hero Coach.
 *
 * Props:
 *  - onDefeat(subId): called when a defeat-kind action's CTA is tapped
 *  - onOpenCoach():   called when a coach-kind action's CTA is tapped
 *  - refreshKey:      bump to force re-fetch (e.g. after a defeat happens elsewhere)
 */
const KIND_STYLE = {
  defeat:    { icon: Sword,       accent: "from-red-500/30 to-red-700/10",       chip: "text-red-300 border-red-500/40 bg-red-500/10" },
  vault:     { icon: Wallet,      accent: "from-amber-500/30 to-amber-700/10",   chip: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  upgrade:   { icon: TrendingUp,  accent: "from-purple-500/30 to-indigo-700/10", chip: "text-purple-300 border-purple-500/40 bg-purple-500/10" },
  review:    { icon: ShieldPlus,  accent: "from-cyan-500/30 to-teal-700/10",     chip: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" },
  coach:     { icon: Sparkles,    accent: "from-emerald-500/30 to-emerald-700/10", chip: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  celebrate: { icon: Sparkles,    accent: "from-emerald-500/30 to-emerald-700/10", chip: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
};

export default function NextBestAction({ refreshKey = 0, onDefeat, onOpenCoach, onConnectBank }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/coach/next-actions");
      setActions(res.data.actions || []);
    } catch (e) {
      console.warn("NBA fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleClick = async (a) => {
    setActing(a.id);
    try {
      if (a.kind === "defeat" && a.target_sub_id) {
        if (onDefeat) onDefeat(a.target_sub_id);
        else {
          await api.post(`/subscriptions/${a.target_sub_id}/defeat`);
          toast.success(`Defeated • saved $${a.impact_amount?.toFixed(2) || ""}/mo`);
        }
        load();
      } else if (a.id === "link-bank") {
        // Connect-bank CTA: hand off to the dashboard-provided handler which
        // scrolls to and clicks the real ConnectBankButton so the same Plaid
        // Link flow runs (including OAuth redirect handling).
        if (onConnectBank) {
          onConnectBank();
        } else {
          // Fallback: click the first Connect Bank button on the page directly.
          const btn = document.querySelector('[data-testid="plaid-connect-button"]');
          if (btn) {
            btn.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => btn.click(), 400);
          } else {
            toast.info("Scroll down and tap Connect Bank in Monthly Battles.");
          }
        }
      } else if (a.cta_route) {
        navigate(a.cta_route);
      } else if (a.kind === "coach" || a.kind === "celebrate") {
        onOpenCoach?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run that action.");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 flex items-center justify-center text-[#7B948B]">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (actions.length === 0) return null;

  return (
    <section className="px-4 mt-2" data-testid="next-best-action">
      <div className="flex items-center justify-between mb-2.5">
        <h3
          className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#94B0A6]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Next best move
        </h3>
        <Sparkles className="w-3.5 h-3.5 text-[#22E07A]" strokeWidth={2.4} />
      </div>
      <div className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-2 snap-x snap-mandatory scrollbar-none">
        {actions.map((a) => {
          const style = KIND_STYLE[a.kind] || KIND_STYLE.coach;
          const Icon = style.icon;
          const busy = acting === a.id;
          return (
            <div
              key={a.id}
              className={`relative shrink-0 w-[82%] sm:w-72 snap-start rounded-3xl border border-[#1B4238] bg-gradient-to-br ${style.accent} p-4 overflow-hidden`}
              data-testid={`nba-card-${a.kind}`}
            >
              <div className="flex items-start gap-2.5 mb-2">
                <div className={`w-9 h-9 rounded-xl border ${style.chip} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-4.5 h-4.5" strokeWidth={2.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-extrabold text-[15px] leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {a.title}
                  </p>
                  {a.impact_amount > 0 && (
                    <span className={`inline-flex items-center mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${style.chip}`}>
                      +${a.impact_amount.toFixed(2)}/mo
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[13px] text-[#C7DAD3] leading-snug mb-3 min-h-[40px]">{a.body}</p>
              <button
                onClick={() => handleClick(a)}
                disabled={busy}
                className="w-full h-10 rounded-2xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-extrabold uppercase tracking-wider text-xs gap-1.5 flex items-center justify-center disabled:opacity-60"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid={`nba-cta-${a.id}`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{a.cta_label} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.8} /></>}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
